import React from 'react'
import { useNavigate } from 'react-router-dom'
import filter from 'lodash/filter'
import isEmpty from 'lodash/isEmpty'
import { Stack } from 'react-bootstrap'
import { useFormikContext } from 'formik'
import type { AddChatMessageArgs, ChatSender, File, User } from '@ztechapps/common.contract'
import type { CustomerSelectCreatableProps, OrderSelectProps } from '@ztechapps/webui.contract'
import type { RecipientOption } from './Context'
import { ChatMessageFormatEnum } from '@ztechapps/common.contract'
import { htmlToPlainText, isValidEmail } from '@ztechapps/common.utils'
import { useError, useRole, useToast } from '@ztechapps/common.hook'
import { Modal } from '@ztechapps/webui.modal'
import { ShowIf } from '@ztechapps/webui.util'
import { BusyButton, SaveCancelButtons } from '@ztechapps/webui.button'
import { RichTextInput, RichTextInputVariantEnum, TextInput } from '@ztechapps/webui.input'
import { Field, Form } from '@ztechapps/webui.form'
import { chatPermissions, useSendChatMessage } from '@ztechapps/chat.common'
import { WebuiIocKey, useInjectComponent } from '@ztechapps/webui.contract'
import { appendMessageLine } from '../Channel/appendMessageLine'
import { StagedAttachments } from '../Chat/StagedAttachments'
import { useFormalizeMessage } from '../Channel/useFormalizeMessage'
import { useRequesterTemplateVariables } from '../Conversation/template-helpers/useRequesterTemplateVariables'
import { useConversationHost } from '../ConversationHost'
import { useConversationInboxContext } from './Context'
import { composerStatusBarInit } from './constants'
import { ModalComposerToolbar } from './ModalComposerToolbar'

// The order/PO selects emit the whole option; we keep it so the picker renders its widget and pull
// the _id at submit.
type OrderOption = { _id?: string }

type ComposeValues = {
  recipient: RecipientOption | null
  order: OrderOption | null
  subject: string
  content: string
  attachments: File[]
}

const initialValues: ComposeValues = {
  recipient: null,
  order: null,
  subject: '',
  content: '',
  attachments: [],
}

// A component of its own so the order picker is looked up only for a host that offers one.
const OrderLinkField = () => {
  const OrderSelect = useInjectComponent<OrderSelectProps>(WebuiIocKey.order.orderSelect)

  return <Field name="order" label="Link to order (optional)" component={OrderSelect} onChange isClearable />
}

type ComposeFieldsProps = {
  /** Closes the surrounding modal on cancel. */
  onCancel: () => void
  sending: boolean
}

// Reads live form values via Formik context so the Send guard is derived from current state, never
// from a value set inside a setState updater.
const ComposeFields = ({ onCancel, sending }: ComposeFieldsProps) => {
  const { values, submitForm, setFieldValue, setValues } = useFormikContext<ComposeValues>()
  const { hasOrderSelect = true } = useConversationHost()
  const CustomerSelectCreatable = useInjectComponent<CustomerSelectCreatableProps>(
    WebuiIocKey.customer.customerSelectCreatable
  )

  const { formalizing, onFormalize } = useFormalizeMessage()
  // The recipient picked or typed here is the requester-to-be — an existing-customer pick carries
  // that customer's `_id`/`profile` while a typed email carries neither, so the hook falls back to
  // the sender. `RecipientOption` models only the fields the send path reads, hence the cast.
  const templateVariables = useRequesterTemplateVariables({ requester: values.recipient as unknown as User })
  // Send and formalize lock each other out — one flag, computed here since the modal has no
  // ChannelProvider `status.busy` to read.
  const busy = sending || formalizing

  const recipientEmail = values.recipient?.profile?.email ?? ''
  const hasBody = !isEmpty(htmlToPlainText(values.content ?? '').trim())
  const canSend = isValidEmail(recipientEmail) && hasBody

  // Formik's setValues takes an updater over the previous values (formik 2.4.2,
  // node_modules/formik/dist/formik.esm.js:689-690), so an attach completion landing after
  // keystrokes still appends below the latest staged text — no ref needed to dodge a
  // render-time closure reading a stale field.
  const insertMessage = (text: string) =>
    setValues((prev) => ({ ...prev, content: appendMessageLine(prev.content ?? '', text) }))

  return (
    <>
      <Modal.Body>
        <Stack gap={3}>
          <Field
            name="recipient"
            label="Recipient"
            help="Pick an existing customer or type an email address."
            component={CustomerSelectCreatable}
            onChange
            showInfo={false}
          />
          <ShowIf condition={hasOrderSelect}>
            <OrderLinkField />
          </ShowIf>
          <Field name="subject" label="Subject" component={TextInput} placeholder="Subject" autoFocus={false} />
          <Field
            name="content"
            label="Message"
            component={RichTextInput}
            onChange
            disabled={busy}
            variant={RichTextInputVariantEnum.Compact}
            height="30vh"
            init={composerStatusBarInit}
            toolbarEnd={
              <ModalComposerToolbar
                message={values.content ?? ''}
                setMessage={(text) => setFieldValue('content', text)}
                insertMessage={insertMessage}
                attachments={values.attachments}
                onAttach={(files) => setFieldValue('attachments', [...values.attachments, ...files])}
                templateVariables={templateVariables}
                disabled={busy}
                formalizing={formalizing}
                onFormalize={onFormalize}
              />
            }
          />
          <StagedAttachments
            attachments={values.attachments}
            onRemove={(fileId) =>
              setFieldValue(
                'attachments',
                filter(values.attachments, (file) => file._id !== fileId)
              )
            }
            disabled={busy}
          />
        </Stack>
      </Modal.Body>
      <Modal.Footer>
        <SaveCancelButtons
          onSave={submitForm}
          onCancel={onCancel}
          isBusy={sending}
          disabled={!canSend || busy}
          saveText="Send"
          saveBusyText="Sending..."
          saveIcon="paper-plane"
        />
      </Modal.Footer>
    </>
  )
}

type ComposeFormProps = {
  /** Closes the surrounding modal once the message is sent or the user cancels. */
  onDone: () => void
}

// Kept as a child so it remounts on every open (react-bootstrap Modal unmounts its content when
// hidden) — that resets the picked recipient / subject / body without manual teardown.
const ComposeForm = ({ onDone }: ComposeFormProps) => {
  const navigate = useNavigate()
  const { showSuccess } = useToast()
  const { chatList, conversationItemRoute, defaultRecipient } = useConversationInboxContext()
  const { sendChat, loading, error } = useSendChatMessage()
  useError(error)

  const onSubmit = async (values: ComposeValues) => {
    const recipient = values.recipient
    if (isEmpty(recipient?.profile?.email)) return

    // On a new conversation the sender IS the recipient — an empty extenalId makes the send path
    // mint a fresh Email chat and deliver to sender.profile.email.
    const sender = {
      profile: { email: recipient?.profile?.email, name: recipient?.profile?.name },
      company: recipient?.company?.name,
    } as ChatSender

    const orderId = values.order?._id

    const result = await sendChat({
      sender,
      subject: values.subject,
      content: values.content,
      attachments: values.attachments,
      extenalId: '',
      isPublic: true,
      format: ChatMessageFormatEnum.html,
      ...(orderId ? { orderId } : {}),
    } as AddChatMessageArgs)

    showSuccess({ message: 'Message sent.' })
    onDone()
    await chatList?.refetch?.()

    const newExternalId = result?.data?.addChatMessage?.chat?.externalId
    if (newExternalId) {
      navigate(conversationItemRoute.altPath({ _id: newExternalId }))
    }
  }

  return (
    <Form initialValues={{ ...initialValues, recipient: defaultRecipient ?? null }} onSubmit={onSubmit}>
      <ComposeFields onCancel={onDone} sending={loading} />
    </Form>
  )
}

export const ComposeConversation = () => {
  const { hasRole: canEdit } = useRole({ roleName: chatPermissions.canEdit.value })

  // Starting a conversation is a write, so a view-only holder gets no entry point to it.
  if (!canEdit) return null

  return (
    <Modal
      size="lg"
      trigger={({ onClick }) => (
        <BusyButton variant="primary" icon="plus" text="New" onClick={onClick} className="text-nowrap" />
      )}
    >
      {({ toggleShowModal }) => (
        <>
          <Modal.Header closeButton>
            <span className="fw-semibold">New conversation</span>
          </Modal.Header>
          <ComposeForm onDone={toggleShowModal} />
        </>
      )}
    </Modal>
  )
}
